// F1a2: guardar las actividades CIIU de la empresa sin borrar y volver a insertar todo.
//
// Antes, StepCIIU borraba todas las filas de company_ciiu_activities y luego insertaba las nuevas: si el insert fallaba,
// la empresa quedaba sin CIIU (y sin nivel de riesgo). Ahora se calcula la diferencia y solo se escriben las filas que
// cambian. Se usan únicamente INSERT y DELETE (las operaciones que la app ya hacía; no consta en el repo una política
// UPDATE para esta tabla), en un orden que nunca deja la empresa sin actividad principal:
//   1. Si la nueva principal ya era secundaria, se borra esa fila (la principal anterior sigue).
//   2. Se inserta la nueva principal (si cambia) y las secundarias nuevas, en una sola sentencia (todo o nada).
//   3. Se borran las quitadas y las principales anteriores (desde aquí ya existe la nueva principal).
//   4. Las principales anteriores que siguen como secundarias se vuelven a insertar como secundarias.
// El cliente no puede hacerlo atómico: si falla el paso 3 o el 4 queda un estado intermedio (con principal), que se
// avisa y se completa al volver a guardar. La versión atómica necesita una función en la base (ver el informe de F1a2).
import { supabase } from '../supabaseClient';
import { mensajeGuardarCiiu } from './unicidad';

export interface FilaCiiu { ciiu_code: string; is_primary: boolean }
export type PasoCiiu =
  | { tipo: 'borrar'; codigos: string[] }
  | { tipo: 'insertar'; filas: FilaCiiu[] };

/** Lo que pide la pantalla: la principal y hasta 3 secundarias, sin repetir ni incluir la principal. */
export function filasDeseadas(principal: string, secundarias: string[]): FilaCiiu[] {
  const sec = Array.from(new Set(secundarias.filter(c => c && c !== principal)));
  return [{ ciiu_code: principal, is_primary: true }, ...sec.map(c => ({ ciiu_code: c, is_primary: false }))];
}

/** true si lo guardado ya es exactamente lo pedido (mismos códigos y misma principal). */
export function mismasActividades(actuales: FilaCiiu[], deseadas: FilaCiiu[]): boolean {
  if (actuales.length !== deseadas.length) return false;
  const mapa = new Map(actuales.map(f => [f.ciiu_code, f.is_primary]));
  return deseadas.every(f => mapa.get(f.ciiu_code) === f.is_primary);
}

/** Pasos para pasar de lo guardado a lo pedido. Vacío si no hay cambios. */
export function pasosGuardarCiiu(actuales: FilaCiiu[], principal: string, secundarias: string[]): PasoCiiu[] {
  const deseadas = filasDeseadas(principal, secundarias);
  if (mismasActividades(actuales, deseadas)) return [];
  const antes = new Map(actuales.map(f => [f.ciiu_code, f.is_primary]));
  const quedan = new Set(deseadas.map(f => f.ciiu_code));
  const secDeseadas = deseadas.filter(f => !f.is_primary).map(f => f.ciiu_code);

  const principalYaEsta = antes.get(principal) === true;
  const principalEraSecundaria = antes.get(principal) === false;
  const principalesViejas = actuales.filter(f => f.is_primary && f.ciiu_code !== principal).map(f => f.ciiu_code);
  const viejasQueSiguen = principalesViejas.filter(c => quedan.has(c));
  const secNuevas = secDeseadas.filter(c => !antes.has(c));
  const quitar = actuales.map(f => f.ciiu_code).filter(c => !quedan.has(c));

  const pasos: PasoCiiu[] = [];
  if (principalEraSecundaria) pasos.push({ tipo: 'borrar', codigos: [principal] });
  const insertar: FilaCiiu[] = [
    ...(principalYaEsta ? [] : [{ ciiu_code: principal, is_primary: true }]),
    ...secNuevas.map(c => ({ ciiu_code: c, is_primary: false })),
  ];
  if (insertar.length) pasos.push({ tipo: 'insertar', filas: insertar });
  const borrar = Array.from(new Set([...quitar, ...viejasQueSiguen]));
  if (borrar.length) pasos.push({ tipo: 'borrar', codigos: borrar });
  if (viejasQueSiguen.length) pasos.push({ tipo: 'insertar', filas: viejasQueSiguen.map(c => ({ ciiu_code: c, is_primary: false })) });
  return pasos;
}

const MSG_A_MEDIAS = 'La actividad principal quedó guardada, pero no se terminaron de actualizar las secundarias. Vuelve a guardar.';

/**
 * Lee lo guardado, calcula los pasos y los ejecuta. Devuelve null si todo salió bien, o el mensaje de error.
 * Si falla antes de que exista la nueva principal, intenta dejar todo como estaba.
 */
export async function guardarActividadesCiiu(companyId: string, principal: string, secundarias: string[]): Promise<string | null> {
  const { data, error: eLeer } = await supabase.from('company_ciiu_activities')
    .select('ciiu_code, is_primary').eq('company_id', companyId);
  if (eLeer) return 'No se pudieron leer las actividades CIIU guardadas: ' + eLeer.message;
  const actuales = (data ?? []) as FilaCiiu[];
  const pasos = pasosGuardarCiiu(actuales, principal, secundarias);
  let principalNuevaGuardada = !pasos.some(p => p.tipo === 'insertar' && p.filas.some(f => f.is_primary));
  const hechos: PasoCiiu[] = [];

  const fallo = async (texto: string) => {
    const base = texto.replace(/\.?$/, '.');
    if (hechos.length === 0) return base + ' No se cambió nada.';
    if (principalNuevaGuardada) return MSG_A_MEDIAS;
    await deshacer(companyId, hechos, actuales);
    return base + ' Se conserva la actividad principal anterior.';
  };

  for (const paso of pasos) {
    if (paso.tipo === 'insertar') {
      const { error } = await supabase.from('company_ciiu_activities')
        .insert(paso.filas.map(f => ({ company_id: companyId, ...f })));
      if (error) return fallo(mensajeGuardarCiiu(error));
      if (paso.filas.some(f => f.is_primary)) principalNuevaGuardada = true;
    } else {
      const { data: borradas, error } = await supabase.from('company_ciiu_activities')
        .delete().eq('company_id', companyId).in('ciiu_code', paso.codigos).select('ciiu_code');
      // Un DELETE que la RLS no deja pasar no da error: devuelve 0 filas. Se trata como fallo.
      if (error || (borradas ?? []).length < paso.codigos.length) {
        return fallo('Error al guardar actividades CIIU: ' + (error?.message ?? 'no se pudo quitar una actividad'));
      }
    }
    hechos.push(paso);
  }
  return null;
}

// Antes de que exista la nueva principal, lo único hecho puede ser el paso 1 (borrar la que era secundaria): se repone.
async function deshacer(companyId: string, hechos: PasoCiiu[], actuales: FilaCiiu[]) {
  const antes = new Map(actuales.map(f => [f.ciiu_code, f.is_primary]));
  const reponer = hechos.flatMap(p => p.tipo === 'borrar' ? p.codigos : [])
    .filter(c => antes.has(c)).map(c => ({ company_id: companyId, ciiu_code: c, is_primary: antes.get(c) as boolean }));
  if (reponer.length) await supabase.from('company_ciiu_activities').insert(reponer);
}
